import { Box, Typography } from "@mui/material";
import { DepthBar } from "./components/DepthBar";

export const formatInr = (n) => {
  if (n == null || n === "") return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(num);
};

export const formatPct = (n) => {
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  return `${num.toFixed(2)}%`;
};

const plColor = (pl) => (pl > 0 ? "success.main" : pl < 0 ? "error.main" : "text.primary");

const profitLossCell = (params, { stacked = false } = {}) => {
  // No quote for this holding - say so rather than showing a P/L of 0.
  if (params.row.profitLoss == null) {
    return <Typography sx={{ color: "text.secondary", fontSize: "0.82rem" }}>No price</Typography>;
  }
  const pl = Number(params.row.profitLoss) || 0;
  const pct = Number(params.row.plnPercentage) || 0;
  const color = plColor(pl);

  if (stacked) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%", lineHeight: 1.25 }}>
        <Typography sx={{ color, fontWeight: 800, fontSize: "0.82rem" }}>{formatInr(pl)}</Typography>
        <Typography sx={{ color, fontWeight: 700, fontSize: "0.72rem", opacity: 0.85 }}>{formatPct(pct)}</Typography>
      </Box>
    );
  }

  return (
    <Typography sx={{ color, fontWeight: 700, fontSize: "0.82rem" }}>
      {formatInr(pl)} ({formatPct(pct)})
    </Typography>
  );
};

/**
 * Desktop column set (>=1024). Same seven facts as before; minWidths were trimmed
 * (920px -> 805px) because the old set plus the 50px selection column overflowed the
 * ~960px of content space available at exactly the `lg` breakpoint.
 */
const fullColumns = [
  { field: "symbol", headerName: "Symbol", flex: 1.6, minWidth: 150, sortable: false },
  { field: "qty", headerName: "QTY", flex: 0.7, minWidth: 70, sortable: false },
  { field: "buyPrice", headerName: "Buy Price", flex: 0.9, minWidth: 100, sortable: false },
  { field: "invested", headerName: "Invested", flex: 1, minWidth: 110, sortable: false },
  { field: "marketPrice", headerName: "C.Price", flex: 0.9, minWidth: 95, sortable: false, valueFormatter: (value) => (value == null ? "—" : value) },
  {
    field: "profitLossCombined",
    headerName: "Profit/Loss",
    flex: 1.3,
    minWidth: 160,
    type: "number",
    align: "left",
    headerAlign: "left",
    valueGetter: (value, row) => Number(row.profitLoss) || 0,
    renderCell: (params) => profitLossCell(params),
  },
  {
    field: "demandSupply",
    headerName: "Demand/Supply",
    flex: 1.2,
    minWidth: 120,
    sortable: false,
    renderCell: (params) => (
      <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center" }}>
        <DepthBar demand={params.row.totalDemand} supply={params.row.totalSupply} />
      </Box>
    ),
  },
];

/**
 * Tablet column set (768-1023). Seven columns will not fit in ~700px of content space,
 * so qty, buy price and depth fold into the Symbol cell rather than being dropped:
 * the row gets taller instead of the table getting wider. Nothing is lost, and there
 * is still no horizontal scroll.
 */
const foldedColumns = [
  {
    field: "symbol",
    headerName: "Symbol",
    flex: 1.7,
    minWidth: 150,
    sortable: false,
    renderCell: (params) => (
      <Box sx={{ width: "100%", py: 0.75, display: "flex", flexDirection: "column", justifyContent: "center", gap: 0.35 }}>
        <Typography noWrap sx={{ fontWeight: 800, fontSize: "0.82rem", lineHeight: 1.2 }} title={params.row.symbol}>
          {params.row.symbol}
        </Typography>
        <Typography noWrap sx={{ fontSize: "0.7rem", color: "text.secondary", lineHeight: 1.2 }}>
          {params.row.qty} × {formatInr(params.row.buyPrice)}
        </Typography>
        <DepthBar demand={params.row.totalDemand} supply={params.row.totalSupply} height={3} />
      </Box>
    ),
  },
  { field: "invested", headerName: "Invested", flex: 1, minWidth: 110, sortable: false },
  { field: "marketPrice", headerName: "C.Price", flex: 0.9, minWidth: 95, sortable: false, valueFormatter: (value) => (value == null ? "—" : value) },
  {
    field: "profitLossCombined",
    headerName: "Profit/Loss",
    flex: 1.3,
    minWidth: 150,
    type: "number",
    align: "left",
    headerAlign: "left",
    valueGetter: (value, row) => Number(row.profitLoss) || 0,
    renderCell: (params) => profitLossCell(params, { stacked: true }),
  },
];

/** `folded` picks the tablet set; default is the full desktop set. */
export const getHoldColumns = ({ folded = false } = {}) => (folded ? foldedColumns : fullColumns);

/** Kept for any existing import of the original constant. */
export const holdColumns = fullColumns;
